import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ACCOUNT_PLATFORM_LABELS, ACCOUNT_TRANSFER_STAGE_LABELS } from "@hybrid-escrow/shared";
import { adminApiClient } from "../../lib/adminApiClient";
import { queryKeys } from "../../lib/queryClient";
import { ErrorBanner, LoadingState, describeError } from "../../components/States";
import { CountdownTimer } from "../../components/CountdownTimer";
import { formatDateTime } from "../../lib/format";

const STAGE_TONE: Record<string, string> = {
  AWAITING_SELLER_HANDOFF: "badge--warning",
  PLATFORM_VERIFYING: "badge--info",
  READY_FOR_BUYER: "badge--success",
  CREDENTIALS_REVEALED: "badge--neutral",
};

/** Read-only view of what the seller submitted — fetched (and decrypted server-side) only once
 *  expanded, since it's a real secret and there's no reason to pull every order's handoff into
 *  memory just to render the queue. */
function SellerHandoffViewer({ orderId }: { orderId: string }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: [...queryKeys.adminAccountTransfers, orderId, "seller-handoff"],
    queryFn: () => adminApiClient.getAccountTransferSellerHandoff(orderId),
  });

  if (isLoading) return <p className="text-sm muted">Loading what the seller submitted…</p>;
  if (isError) return <p className="text-sm muted">{describeError(error)}</p>;
  if (!data) return null;

  return (
    <div className="card stack" style={{ background: "var(--color-bg-subtle, transparent)" }}>
      <p className="text-sm muted" style={{ margin: 0 }}>
        Submitted by the seller {formatDateTime(data.submittedAt)}
      </p>
      {data.username && (
        <div className="row row--between">
          <span className="muted text-sm">Username / email</span>
          <span className="mono">{data.username}</span>
        </div>
      )}
      {data.password && (
        <div className="row row--between">
          <span className="muted text-sm">Password</span>
          <span className="mono">{data.password}</span>
        </div>
      )}
      {data.notes && (
        <div className="stack" style={{ gap: "var(--space-1)" }}>
          <span className="muted text-sm">Notes</span>
          <p className="text-sm" style={{ margin: 0, whiteSpace: "pre-wrap" }}>
            {data.notes}
          </p>
        </div>
      )}
    </div>
  );
}

/** Inline credential-submission form for one order — kept as its own component so its draft
 *  username/password/notes state doesn't leak between rows or get wiped by every list refetch. */
function CredentialsForm({ orderId, onSubmitted }: { orderId: string; onSubmitted: () => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [notes, setNotes] = useState("");

  const submit = useMutation({
    mutationFn: () =>
      adminApiClient.submitAccountTransferCredentials(orderId, {
        username: username.trim() || undefined,
        password: password || undefined,
        notes: notes.trim() || undefined,
      }),
    onSuccess: () => {
      setUsername("");
      setPassword("");
      setNotes("");
      onSubmitted();
    },
  });

  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        submit.mutate();
      }}
    >
      <div className="row" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
        <div className="field" style={{ flex: 1, minWidth: 160, marginBottom: 0 }}>
          <label htmlFor={`username-${orderId}`}>Username / email</label>
          <input
            id={`username-${orderId}`}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            maxLength={200}
          />
        </div>
        <div className="field" style={{ flex: 1, minWidth: 160, marginBottom: 0 }}>
          <label htmlFor={`password-${orderId}`}>Password</label>
          <input
            id={`password-${orderId}`}
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            maxLength={500}
          />
        </div>
      </div>
      <div className="field" style={{ marginBottom: 0 }}>
        <label htmlFor={`notes-${orderId}`}>Notes (instructions for the buyer)</label>
        <textarea
          id={`notes-${orderId}`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          maxLength={4000}
          placeholder="e.g. You are now Primary Owner of the Brand Account — no password needed."
        />
      </div>
      {submit.isError && (
        <p className="field-error-text" role="alert">
          {describeError(submit.error)}
        </p>
      )}
      <div className="row">
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={submit.isPending || (!username.trim() && !password && !notes.trim())}
        >
          {submit.isPending ? "Submitting…" : "Submit credentials to buyer"}
        </button>
      </div>
    </form>
  );
}

export function AdminAccountTransfersPage() {
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.adminAccountTransfers,
    queryFn: () => adminApiClient.listAccountTransfers({ limit: 100 }),
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.adminAccountTransfers });
  }

  const advanceToVerifying = useMutation({
    mutationFn: (orderId: string) => adminApiClient.advanceAccountTransferToVerifying(orderId),
    onSuccess: invalidate,
  });

  return (
    <div className="stack">
      <div>
        <h1>Account transfers</h1>
        <p className="muted text-sm">
          No platform we support (YouTube, X, TikTok) exposes an API that can safely automate the
          actual account handoff — that's why every order here needs a person to accept the
          seller's handoff, verify the account, and hand final credentials to the buyer. See each
          listing's platform for the exact steps to follow before submitting credentials.
        </p>
      </div>

      {isLoading && <LoadingState label="Loading account transfers…" />}
      {isError && <ErrorBanner error={error} onRetry={() => void refetch()} />}

      {data && data.items.length === 0 && <p className="muted">No account-transfer orders yet.</p>}

      {data &&
        data.items.map((item) => (
          <div key={item.id} className="card stack">
            <div className="row row--between">
              <div>
                <strong>{item.listingTitle}</strong>
                <p className="text-sm muted" style={{ margin: 0 }}>
                  {item.accountPlatform ? ACCOUNT_PLATFORM_LABELS[item.accountPlatform] : "—"} ·
                  Seller: {item.sellerUsername ?? "—"} · Buyer: {item.buyerUsername ?? "—"} ·
                  Opened {formatDateTime(item.createdAt)}
                </p>
                {item.buyerGoogleEmail && (
                  <p className="text-sm muted" style={{ margin: 0 }}>
                    Buyer's Google account (tell the seller to invite this):{" "}
                    <span className="mono">{item.buyerGoogleEmail}</span>
                  </p>
                )}
              </div>
              <div className="row" style={{ gap: "0.5rem" }}>
                <span className="badge badge--neutral">{item.financialStatus}</span>
                <span className={`badge ${STAGE_TONE[item.accountTransferStage] ?? "badge--neutral"}`}>
                  {ACCOUNT_TRANSFER_STAGE_LABELS[item.accountTransferStage]}
                </span>
                {(item.accountTransferStage === "AWAITING_SELLER_HANDOFF" ||
                  item.accountTransferStage === "PLATFORM_VERIFYING") && (
                  <span className={`badge ${item.sellerHandoffSubmitted ? "badge--success" : "badge--warning"}`}>
                    {item.sellerHandoffSubmitted ? "Seller submitted" : "Waiting on seller"}
                  </span>
                )}
                {item.accountTransferStage !== "CREDENTIALS_REVEALED" && item.disputeDeadline && (
                  <CountdownTimer deadlineIso={item.disputeDeadline} />
                )}
              </div>
            </div>

            {item.accountTransferStage === "CREDENTIALS_REVEALED" ? (
              <p className="text-sm muted">
                The buyer has already claimed their credentials — nothing left to do here.
              </p>
            ) : (
              <div className="row row--between">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                >
                  {expandedId === item.id ? "Close" : "Manage"}
                </button>
                {item.accountTransferStage === "AWAITING_SELLER_HANDOFF" && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={advanceToVerifying.isPending}
                    onClick={() => advanceToVerifying.mutate(item.id)}
                  >
                    Mark as verifying
                  </button>
                )}
              </div>
            )}

            {expandedId === item.id && item.accountTransferStage !== "CREDENTIALS_REVEALED" && (
              <>
                {item.sellerHandoffSubmitted && <SellerHandoffViewer orderId={item.id} />}
                <CredentialsForm orderId={item.id} onSubmitted={invalidate} />
              </>
            )}
          </div>
        ))}
    </div>
  );
}
